import React from 'react';
import { createPortal } from 'react-dom';
import './TooltipTrigger.css';

import { ReactComponent as InfoIcon } from '../../assets/icons/info.svg';

const TooltipTrigger = ({ text }) => {
  const [visible, setVisible] = React.useState(false);
  const [position, setPosition] = React.useState({ left: 0, top: 0 });
  const wrapperRef = React.useRef(null);
  const triggerRef = React.useRef(null);
  const tooltipId = React.useId();

  const updatePosition = React.useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger || typeof window === 'undefined') return;

    const rect = trigger.getBoundingClientRect();
    const margin = 8;
    const tooltipMaxWidth = Math.min(window.innerWidth - (margin * 2), window.innerWidth <= 768 ? 320 : 256);
    const left = Math.min(
      Math.max(margin, rect.left + (rect.width / 2) - (tooltipMaxWidth / 2)),
      window.innerWidth - tooltipMaxWidth - margin
    );

    setPosition({
      left,
      top: rect.bottom + 10,
    });
  }, []);

  const openTooltip = React.useCallback(() => {
    if (!text) return;
    updatePosition();
    setVisible(true);
  }, [text, updatePosition]);

  const closeTooltip = React.useCallback(() => {
    setVisible(false);
  }, []);

  React.useEffect(() => {
    if (!visible) return undefined;

    const handlePointerDown = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        closeTooltip();
      }
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        closeTooltip();
      }
    };
    const handleViewportChange = () => updatePosition();

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown, true);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown, true);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [closeTooltip, visible, updatePosition]);

  return (
    <span className="tooltip-trigger" ref={wrapperRef}>
      <button
        type="button"
        className="tooltip-trigger__trigger"
        aria-label="Field help"
        aria-describedby={visible ? tooltipId : undefined}
        ref={triggerRef}
        aria-expanded={visible}
        aria-haspopup="true"
        onMouseEnter={openTooltip}
        onMouseLeave={closeTooltip}
        onBlur={closeTooltip}
        onClick={() => {
          if (visible) {
            closeTooltip();
          } else {
            openTooltip();
          }
        }}
      >
        <InfoIcon className="icon base image" />
      </button>

      {visible && typeof document !== 'undefined' && createPortal(
        <span
          id={tooltipId}
          role="tooltip"
          className="tooltip tooltip-floating no-triangle tooltip-trigger__tooltip"
          style={{ left: position.left, top: position.top }}
        >
          {text}
        </span>,
        document.body
      )}
    </span>
  );
};

export default TooltipTrigger;
